import React from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import dayjs from 'dayjs'
import { Icon, Text } from '@components'
import { Theme } from '@theme'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'
import { Scope } from '@utils'
import { useGetProfileById } from '../../../hooks/api/queries/useGetProfileById'
import { NotificationActions } from './NotificationActions'
import { frenchDe, richParts, RichSentence } from './richText'
import { useReachOut } from '../useReachOut'

/** What was just done from the page, shown in green until the page is left. */
export type Confirmation =
  | {
      kind: 'done'
      /** The follow-up notification (its id is the follow-up's). */
      id: string
      personId: string
      previousDate: string
      nextDate: string | null
    }
  | { kind: 'accepted'; id: string; personId: string; scope: Scope }

const capitalize = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

export const NotificationConfirmation = ({
  confirmation,
  onUndo,
}: {
  confirmation: Confirmation
  /** « Annuler » (after « C'est fait »). */
  onUndo?: () => void
}) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t, currentLanguage } = useLanguage()
  const navigation = useNavigation()
  const { openConversation } = useReachOut()
  const { data: person } = useGetProfileById(confirmation.personId)
  const name = person?.fullName || person?.firstName || ''

  const openProfile = () =>
    (navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: confirmation.personId },
    })

  let title: React.ReactNode = null
  let chip: string | null = null
  let detail: string | null = null
  let actions: React.ComponentProps<typeof NotificationActions>['actions'] = []

  if (confirmation.kind === 'done') {
    title = (
      <RichSentence
        parts={richParts(t, 'notifications.titles.done', { name, de: frenchDe(name, currentLanguage) }, ['name'])}
        color={theme.colors.black}
        style={styles.flexText}
      />
    )
    if (confirmation.nextDate) {
      chip = t('notifications.details.nextReminder', {
        day: capitalize(dayjs(confirmation.nextDate).format('dddd D MMMM')),
      })
    } else {
      detail = t('notifications.details.finished')
    }
  } else {
    const scope =
      confirmation.scope === Scope.PROFESSIONAL
        ? t('notifications.scope.PROFESSIONAL')
        : t('notifications.scope.PRIVATE')
    title = (
      <RichSentence
        parts={richParts(t, 'notifications.titles.acceptedByMe', { name, scope }, ['name', 'scope'])}
        color={theme.colors.black}
        style={styles.flexText}
      />
    )
    actions = [
      {
        key: 'write',
        label: t('notifications.actions.write'),
        icon: 'messageCircle',
        onPress: () => void openConversation(confirmation.personId),
      },
      { key: 'see-profile', label: t('notifications.actions.seeProfile'), icon: 'user', onPress: openProfile },
    ]
  }

  return (
    <View style={styles.card} testID={`notification-confirmation-${confirmation.kind}`}>
      <View style={styles.leading}>
        <Image source={person?.avatarUrl ? { uri: person.avatarUrl } : defaultImage} style={styles.avatar} />
        <View style={styles.badge}>
          <Icon name="check" size={theme.iconSize.extraSmall} color={theme.colors.green} />
        </View>
      </View>
      <View style={styles.content}>
        <View style={styles.titleRow}>
          {title}
          <Text type="microText" color={theme.colors.greyBlue} style={styles.time}>
            {t('notifications.justNow')}
          </Text>
        </View>
        {chip ? (
          <View style={styles.chip}>
            <Icon name="clock" size={theme.iconSize.extraSmall} color={theme.colors.greyBlue} />
            <Text type="bodyExtraSmallBold" color={theme.colors.greyBlue}>
              {chip}
            </Text>
          </View>
        ) : null}
        {detail || onUndo ? (
          <View style={styles.detailRow}>
            {detail ? (
              <Text type="bodySmall" color={theme.colors.greyBlue}>
                {detail}
              </Text>
            ) : null}
            {onUndo ? (
              <TouchableOpacity
                testID="notification-action-undo"
                accessibilityRole="button"
                onPress={onUndo}
              >
                <Text type="bodySmallBold" color={theme.colors.primary}>
                  {t('notifications.actions.undo')}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}
        <NotificationActions actions={actions} />
      </View>
    </View>
  )
}

const AVATAR_SIZE = 52

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.md,
      borderRadius: theme.border.large,
      borderWidth: 1,
      backgroundColor: theme.colors.green + '0F',
      borderColor: theme.colors.green + '59',
    },
    leading: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      marginRight: theme.spacing.md,
    },
    avatar: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      backgroundColor: theme.colors.grey10,
    },
    badge: {
      position: 'absolute',
      right: -2,
      bottom: -2,
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: theme.colors.green,
      backgroundColor: theme.colors.white,
    },
    content: {
      flex: 1,
      gap: theme.spacing.sm,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    flexText: {
      flex: 1,
      marginRight: theme.spacing.sm,
    },
    time: {
      marginTop: theme.spacing.xxs,
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.border.small,
      backgroundColor: theme.colors.grey10,
    },
    detailRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.md,
    },
  })
